import { useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import {
  AppContent,
  AppPage,
  Breadcrumbs,
  ErrorComponents,
  OpenSidebarButton,
  PageHeader,
  PageTabs,
  isAccess,
  isResourceAccess,
  useFeatureToggle,
  useTitle,
  useRecentlyViewed,
} from '@css-xp/base';
// @ts-ignore
import InvoiceGroupIcon from '@css-xp/base/assets/img/icon-folder.svg';
import { GetUpdatePaymentMethodLinkPane, SetPaymentMethod } from '@css-xp/base/common-sidebars';
import { PaymentMethodsTable, ProductTab } from '@css-xp/base/common-tabs';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { FetchInvoiceGroupQuery, FetchInvoiceGroupQueryVariables, InvoiceGroupProduct } from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { updateDefaultPaymentMethod } from '@atlassian/temp-proxies';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import { isConfluenceProduct, isJiraProduct } from '@atlassian/cssxp-utils-product';
import { InvoiceGroupSummary } from './components/invoice-group-summary';
import { EditInvoiceGroupPane } from './components/sidebar/edit-invoice-group-pane';
import InvoicesTabIG from './invoice-tab-ig';
import { fetchInvoiceGroupQuery } from './queries/fetch-invoice-group';
import { fetchProductsIDForInvoiceGroup } from './queries/fetch-product-id-for-billing-group';
import { ENABLE_RECENTLY_VISITED_HISTORY } from '@atlassian/cssxp-utils-generic';

export enum SIDEBAR {
  EDIT_INVOICE_GROUP = 'edit-invoice-group',
  GET_PAYMENT_METHOD_LINK = 'get-payment-method-link',
  SET_CARD_PAYMENT_DEFAULT = 'setCardPaymentDefault',
}

const allowedSidebars = {
  [SIDEBAR.EDIT_INVOICE_GROUP]: EditInvoiceGroupPane,
  [SIDEBAR.GET_PAYMENT_METHOD_LINK]: GetUpdatePaymentMethodLinkPane,
  [SIDEBAR.SET_CARD_PAYMENT_DEFAULT]: SetPaymentMethod,
};

interface InvoiceGroupPageProps {
  title?: string;
}

export const InvoiceGroupPage = ({ title }: InvoiceGroupPageProps) => {
  const { id } = useParams<{ id: string }>();
  const [isEnabled] = useFeatureToggle();
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(id, title);
  }
  const isPaymentOnTermsMethodAllowed = isAccess(Permissions.PAYMENT_METHOD_ON_TERM_WRITE);
  const [paymentOnTermsDisabled, setPaymentOnTermsDisabled] = useState<boolean>(!isPaymentOnTermsMethodAllowed);
  const { data, loading, error, refetch } = useQuery<FetchInvoiceGroupQuery, FetchInvoiceGroupQueryVariables>(
    fetchInvoiceGroupQuery,
    {
      variables: {
        id,
      },
    }
  );

  const { updateRecentlyViewed } = useRecentlyViewed();
  React.useEffect(() => {
    if (data && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          invoiceGroupDisplayName: getInvoiceGroupName(),
          paymentMethods: data.invoiceGroup?.defaultPaymentMethod?.type,
        },
      });
    }
  }, [data, title]);

  // Identifies if any of the products are jira product and should payment or terms be enabled or disbaled for IG.
  React.useEffect(() => {
    (async () => {
      const { data } = await graphqlClient().query({
        query: fetchProductsIDForInvoiceGroup,
        variables: {
          id: id ?? '',
          fetchEntitlementProductIDs: true,
          skip: !id,
        },
        fetchPolicy: 'no-cache',
      });
      const productRecords = data?.invoiceGroup?.products.nodes as Array<InvoiceGroupProduct>;
      let disablePaymentOnTermFlag: Array<InvoiceGroupProduct> = [];
      if (productRecords) {
        disablePaymentOnTermFlag = productRecords.filter(
          (item) => isConfluenceProduct(item.productId) || isJiraProduct(item.productId)
        );
      }
      if (disablePaymentOnTermFlag.length) {
        setPaymentOnTermsDisabled(true);
      }
    })();
  }, []);

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;
  if (!data?.invoiceGroup) {
    return (
      <ErrorComponents.ErrorPanel
        error={new ErrorComponents.NotFoundError(`Unable to find invoice group with ID: ${id}`)}
      />
    );
  }
  const invoiceGroupDisplayName = data.invoiceGroup?.name;
  const transactionAccountDisplayName = data.invoiceGroup.transactionAccount?.displayName;
  const transactionAccountId = data.invoiceGroup.transactionAccount?.id;

  const getInvoiceGroupName = () => {
    if (!invoiceGroupDisplayName) {
      return 'Unnamed invoice group';
    }
    return invoiceGroupDisplayName;
  };

  return (
    <AppPage id={'invoice-group-page'} allowedSidebars={allowedSidebars}>
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.INVOICE_GROUP_SCREEN,
          attributes: {
            invoiceGroupDisplayName,
            transactionAccountId,
            id,
            entityName: title,
          },
        }}
      />
      <Breadcrumbs
        items={[
          {
            title: transactionAccountDisplayName || 'Billing account',
            href: `/transaction-account/${encodeURIComponent(transactionAccountId || '')}`,
          },
          { title: getInvoiceGroupName() || '', href: '#' },
        ]}
      />
      <AppContent>
        <PageHeader
          /*// @ts-ignore */
          icon={<img src={InvoiceGroupIcon} height={'30px'} alt="IG" />}
          header={getInvoiceGroupName()}
          subheader={''}
          actions={
            isResourceAccess(Resources.INVOICE_GROUP_ALL) && (
              <>
                {isAccess(Permissions.INVOICE_GROUP_WRITE) && (
                  <OpenSidebarButton
                    sidebar={SIDEBAR.EDIT_INVOICE_GROUP}
                    sidebarOptions={{
                      sidebarWidth: '50%',
                      isOverlay: true,
                    }}
                    payload={{ ...data?.invoiceGroup, refetch }}
                    testId="edit-invoice-group-button"
                    label="Edit invoice group"
                  >
                    Edit invoice group
                  </OpenSidebarButton>
                )}

                <OpenSidebarButton
                  sidebar={SIDEBAR.GET_PAYMENT_METHOD_LINK}
                  sidebarOptions={{
                    sidebarWidth: '50%',
                    isOverlay: true,
                  }}
                  payload={{
                    updatePaymentMethodLink: data?.invoiceGroup.updatePaymentMethodLink,
                  }}
                  testId="add-payment-method-button"
                  label="Add payment method"
                >
                  Add payment method
                </OpenSidebarButton>
                <OpenSidebarButton
                  sidebar={SIDEBAR.SET_CARD_PAYMENT_DEFAULT}
                  sidebarOptions={{
                    sidebarWidth: '50%',
                    isOverlay: true,
                  }}
                  payload={{
                    transactionAccountId,
                    invoiceGroupId: id,
                    isPaymentOnTermsDisabled: paymentOnTermsDisabled,
                    refetch,
                    submitAction: updateDefaultPaymentMethod,
                  }}
                  testId="set-payment-method-button"
                  label="Set payment method"
                >
                  Set payment method
                </OpenSidebarButton>
              </>
            )
          }
        />
        <InvoiceGroupSummary invoiceGroup={data} />
        <PageTabs>
          <ProductTab invoiceGroupId={id} label="Products" />
          <PaymentMethodsTable
            transactionAccountId={transactionAccountId}
            submitAction={updateDefaultPaymentMethod}
            invoiceGroupId={id}
            label="Default payment method"
            isPaymentOnTermsMethodAllowed={isPaymentOnTermsMethodAllowed}
          />
          <InvoicesTabIG id={id} label="Invoices" />
        </PageTabs>
      </AppContent>
    </AppPage>
  );
};
